<template>
    <div class="error-license-admin">
        <error-license-lock
            v-if="isLicensesAvailable"
            :title="$t.__('pages.errors.ErrorLicenseAdmin.welcome')"
            :subtitles="[
                $t.__('pages.errors.ErrorLicenseAdmin.before_tools_access_attribute_licence'),
            ]"
            :isDisplayed="!invitationRoute"
        >
            <div v-if="!invitationRoute" class="error-license-admin__content">
                <div class="error-license-admin__select">
                    <CompassSelect
                        :key="licenseSelectKey"
                        :modelValue="license"
                        :disabled="loading"
                        :placeholder="labels.select.placeholder"
                        size="large"
                        @change="updateLicense"
                    >
                        <CompassDropdownOption
                            v-for="availableLicense in availableLicenses"
                            :key="availableLicense.value"
                            :disabled="availableLicense.disabled"
                            :label="availableLicense.label"
                            :value="availableLicense.value"
                        />
                    </CompassSelect>
                </div>

                <CompassButton
                    :label="labels.buttons.continue"
                    size="large"
                    class="error-license-admin__button"
                    :disabled="!isContinueButtonEnabled"
                    @click="addLicenseForStaff"
                />

                <div class="error-license-admin__no-license">
                    <p
                        class="error-license-admin__no-license-text typographies-paragraph-medium-semibold"
                    >
                        {{ $l10n.__("Vous n'avez pas besoin de licence ?") }}
                    </p>
                    <CompassPlainButton
                        class="error-license-admin__no-license-link"
                        :label="labels.buttons.link"
                        @click.prevent="goLicenseAssignmentStep"
                    />
                </div>
            </div>

            <license-assign v-else :licenseChosen="license" @back="back" />
        </error-license-lock>

        <error-license-admin-no-license v-else />
    </div>
</template>

<script>
import { goToUrl } from 'vueDir/helpers.js';
import LicenseAssign from 'vcomponents/pages/errors/LicenseAssign.vue';
import ErrorLicenseLock from 'vcomponents/pages/errors/ErrorLicenseLock.vue';
import ErrorLicenseAdminNoLicense from 'vcomponents/pages/errors/ErrorLicenseAdminNoLicense.vue';
import { createNamespacedHelpers } from 'vuex';
const { mapState: mapStateUser } = createNamespacedHelpers('user');
const { mapState, mapActions } = createNamespacedHelpers('subscriptionManager');
const noLicenseValue = 'null';

export default {
    components: {
        ErrorLicenseAdminNoLicense,
        ErrorLicenseLock,
        LicenseAssign,
    },
    data() {
        return {
            licenseSelectKey: 0,
            disabled: true,
            loading: false,
            license: noLicenseValue,
            invitationRoute: false,
            localAvailableLicensesTotal: 0,
            labels: {
                select: {
                    placeholder: this.$l10n.__('Sélectionnez votre licence'),
                },
                buttons: {
                    continue: this.$l10n.__('Continuer'),
                    link: this.$l10n.__('Inviter vos collaborateurs et gérer votre abonnement'),
                },
            },
        };
    },
    computed: {
        ...mapState(['availableLicenses', 'staffs', 'licenses']),
        isContinueButtonEnabled() {
            return this.license !== noLicenseValue && !this.disabled;
        },
        isLicensesAvailable() {
            const licenses = this.availableLicenses.filter((license) => license.value !== 'null');

            //Mono user and only one license available
            if (this.staffs.length <= 1) {
                return true;
            }

            //No licenses loaded yet
            if (licenses.length < 1) {
                return true;
            }

            const licenseNumberAvailable = licenses.reduce((accumulator, license) => {
                if (!license.disabled) {
                    return (accumulator += 1);
                }
                return accumulator;
            }, 0);
            return licenseNumberAvailable > 0;
        },
        ...mapStateUser({
            staffId: 'id',
        }),
    },
    watch: {
        invitationRoute() {
            if (this.localAvailableLicensesTotal !== this.availableLicenses.length) {
                this.setAvailableLicenses();
            }
        },
        availableLicenses() {
            ++this.licenseSelectKey;
        },
        staffs() {
            for (const staff of this.staffs) {
                if (staff.id === this.staffId) {
                    this.license = staff.license === null ? noLicenseValue : staff.license;
                    this.disabled = this.license === noLicenseValue;
                }
            }
        },
        licenses() {
            this.setAvailableLicenses();
        },
    },
    created() {
        if (localStorage.getItem('licenseManager')) {
            this.invitationRoute = true;
        }
        this.getAvailableLicenses();
    },
    methods: {
        goToUrl,
        ...mapActions(['getSubscription', 'setAvailableLicenses', 'setLicenseForStaff']),
        getAvailableLicenses() {
            this.loading = true;
            this.getSubscription()
                .then(() => {
                    this.setAvailableLicenses();
                    this.localAvailableLicensesTotal = this.availableLicenses.length;
                })
                .catch(() => {
                    Push.error(
                        this.$l10n.__('Une erreur est survenue, merci de réessayer plus tard'),
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        /**
         * On click, display the popup view.
         * save in local user preferences, if no license, display the popup as default.
         */
        goLicenseAssignmentStep() {
            this.invitationRoute = !this.invitationRoute;
            if (this.invitationRoute && !localStorage.getItem('licenseManager')) {
                localStorage.setItem('licenseManager', true);
            }
        },
        updateLicense(newValue) {
            if (newValue === noLicenseValue) {
                this.deleteLicenseForStaff();
                this.disabled = true;
            } else {
                this.license = newValue;
                this.disabled = false;
            }
        },
        addLicenseForStaff() {
            this.loading = true;
            this.setLicenseForStaff({
                staffId: this.staffId,
                licenseId: this.license,
            })
                .then(() => {
                    if (this.staffs.length <= 1) {
                        this.goToUrl('/home');
                        return;
                    }

                    this.disabled = false;
                    this.goLicenseAssignmentStep();
                })
                .catch(() => {
                    Push.error(
                        this.$l10n.__('Une erreur est survenue, merci de réessayer plus tard'),
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        deleteLicenseForStaff() {
            if (this.license !== noLicenseValue) {
                this.loading = true;

                this.setLicenseForStaff({
                    staffId: this.staffId,
                    licenseId: null,
                    previousLicenseId: this.license,
                })
                    .then(() => {
                        this.license = noLicenseValue;
                        this.disabled = true;
                    })
                    .catch(() => {
                        Push.error(
                            this.$l10n.__('Une erreur est survenue, merci de réessayer plus tard'),
                        );
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            }
        },
        back() {
            localStorage.removeItem('licenseManager');
            this.deleteLicenseForStaff();
            this.invitationRoute = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.error-license-admin {
    min-height: calc(100vh - var(--spacing-7));

    &__content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--spacing-5);
    }

    &__select {
        &::v-deep .compass-select {
            min-width: 325px;
        }
    }

    &__no-license {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        &-text {
            color: var(--colors-common-text-standard-default);
            margin: 0;
        }
    }
}
</style>

<style>
#contentDiv.has-sidebar-closed:has(.error-license-admin) {
    padding: var(--spacing-5) !important;
}
</style>
